import React, {
 useRef, useEffect, useState, useContext 
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { PAYMENT_STATE, updatePaymentStates } from '@apps/redux/features/PayAndReviewSlice'
import {
 Icon, Autocomplete, CardInfo, Typography, Tooltip 
} from '@telus/koodo-ui-kit'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import MarkdownRenderer from '@apps/components/MarkdownRenderer'
import { getAddressSearchDebounceMs } from '@apps/utils/addressSearch'

const CreditCardFormEPS = ({
  cardErrorMessage,
  cardNumberLabel,
  postalCodeLabel,
  postalErrorMessage,
  onChangePostal,
  onBlurPostal,
  valuePostal,
  expiryDateLabel,
  expiryErrorMessage,
  labelSecurityCode,
  cvvErrorMessage,
  onFocusPostal,
  billingAddressLabel,
  billingAddressPlaceholder,
  billingAddressLoadingLabel,
  billingAddressNoResults,
  billingAddressErrorMessage,
  onBlurBillingAddress,
  postalCodeTooltip,
  cvvTooltip,
  validCreditCardContent
}) => {
  const dispatch = useDispatch()
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [isAddressLoading, setIsAddressLoading] = useState(false)
  const [addressError, setAddressError] = useState(false)
  const [isAddressFocused, setIsAddressFocused] = useState(false)
  
  const appContext = useContext(ApplicationContext)
  const { [APPLICATION_STATE.SERVICES]: brandServices, [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage } = appContext
  const paymentState = useSelector((state) => state.payAndReviewPage)
  const pageContent = useSelector((state) => state.common?.content?.pageContent)
  const addressDebounceMs = getAddressSearchDebounceMs(pageContent, currentLanguage)

  const {
    [PAYMENT_STATE.PAYMENT_SCROLL_TO_ERR_FIELD]: paymentScrollToErrField,
    [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: billingAddress,
    [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS_SELECTED]: selectedBillingAddress
  } = paymentState

  const updatePaymentStore = (payload) => dispatch(updatePaymentStates(payload))

  const ccRef = useRef(null)
  const searchDebounceRef = useRef(null)
  const expiryRef = useRef(null)
  const cvvRef = useRef(null)
  const postalRef = useRef(null)
  const billingRef = useRef(null)

  useEffect(() => () => clearTimeout(searchDebounceRef.current), [])

  // The card iframes and the postal field are tied to this component instance
  // and come back empty when it remounts; the billing address lives in Redux clear
  // it on unmount so the field matches the other inputs on return.
  useEffect(() => () => {
    dispatch(updatePaymentStates({
      [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: '',
      [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS_SELECTED]: ''
    }))
  }, [dispatch])

  useEffect(() => {
    if (paymentScrollToErrField) {
      switch (paymentScrollToErrField) {
        case 'cardNumber':
          ccRef.current.scrollIntoView({
            behavior: 'smooth'
          })
          break
        case 'expiryDate':
          expiryRef.current.scrollIntoView({
            behavior: 'smooth'
          })
          break
        case 'cvv':
          cvvRef.current.scrollIntoView({
            behavior: 'smooth'
          })
          break
        case 'postal':
          postalRef.current.scrollIntoView({
            behavior: 'smooth'
          })
          break
        case 'billingAddress':
          billingRef.current.scrollIntoView({
            behavior: 'smooth'
          })
          break
        default:
          postalRef.current.scrollIntoView({
            behavior: 'smooth'
          })
          break
      }
    }
  }, [paymentScrollToErrField])

  const handleAddressChange = (value) => {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: value })
    setAddressError(false)
    clearTimeout(searchDebounceRef.current)
    if (!value) {
      setAddressSuggestions([])
      return
    }
    if (addressSuggestions.some((opt) => opt.label === value)) return
    searchDebounceRef.current = setTimeout(async () => {
      setIsAddressLoading(true)
      try {
        const response = await brandServices.getAddress('find', value, null, currentLanguage?.toUpperCase(), 7)
        const results = response?.find || []
        setAddressSuggestions(
          results.map((item) => ({
            id: item.Id,
            label: item.Text ? `${item.Text}, ${item.Description}` : item.Description
          }))
        )
      } finally {
        setIsAddressLoading(false)
      }
    }, addressDebounceMs)
  }

  const handleAddressSelect = async (id) => {
    clearTimeout(searchDebounceRef.current)
    const response = await brandServices.getAddress('retrieve', null, id, currentLanguage?.toLowerCase())
    const results = response?.retrieve || []
    const lang = currentLanguage?.toUpperCase() === 'EN' ? 'ENG' : 'FRE'
    const match = results.find((r) => r.Language === lang) || results[0]
    if (match) {
      const fullAddress = [match.Line1, match.City, match.ProvinceCode, match.PostalCode]
        .filter(Boolean)
        .join(', ')
      // Remember the resolved value so both blur and submit-time validation
      // recognize it as a valid selection; it lives in Redux because the
      // parent (EPSCard) is what gates Continue.
      updatePaymentStore({
        [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: fullAddress,
        [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS_SELECTED]: fullAddress
      })
      setAddressError(false)
    }
    setAddressSuggestions([])
  }

  // Flag an error on blur when the field holds text that isn't the address
  // resolved from a valid dropdown selection; an empty field surfaces the
  // required error via onBlurBillingAddress, matching the postal field.
  const handleAddressBlur = () => {
    setIsAddressFocused(false)
    setAddressError(Boolean(billingAddress) && billingAddress !== selectedBillingAddress)
    onBlurBillingAddress?.()
  }

  return (
    <form>
      {/* Card Number */}
      <div ref={ccRef}>
        <label className="block text-copy text-xs font-semibold font-body mb-1" htmlFor="CardNumber">
          <Typography variant={{ size: 'h6' }}>{cardNumberLabel}</Typography>
        </label>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-5">
          <div className="relative w-full md:flex-1">
            <div id="CardNumber" className={`bg-white ${cardErrorMessage ? 'border-error' : 'border-black'} border-solid border h-12 rounded-sm`} aria-label={cardNumberLabel} />
            <div className="absolute left-3 top-6 -translate-y-1/2 pointer-events-none flex items-center">
              <Icon icon="CreditCard" className="h-5 w-5" />
            </div>
            {cardErrorMessage && (
              <div className="flex items-center gap-2 mt-1 px-4 py-3" style={{ backgroundColor: 'var(--color-error-light)' }}>
                <Icon icon="ExclamationOctagon" className="h-4" variant={{ color: 'danger' }} />
                <Typography variant={{ size: 'small', colour: 'danger' }}>{cardErrorMessage}</Typography>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0 md:h-12">
            <div className="inline-flex items-center justify-center rounded-md border border-[#E3E6E8] bg-white px-1">
              <Icon icon="MasterCard" variant={{ size: 'large' }} />
            </div>
            <div className="inline-flex items-center justify-center rounded-md border border-[#E3E6E8] bg-white px-1">
              <Icon icon="Visa" variant={{ size: 'large' }} />
            </div>
            <div className="inline-flex items-center justify-center rounded-md border border-[#E3E6E8] bg-white px-1">
              <Icon icon="Amex" variant={{ size: 'large' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Info card — full width, below card number */}
      {validCreditCardContent && (
        <div className="mt-4">
          <CardInfo>
            <div className="pb-2">
              <Typography>
                <MarkdownRenderer markdown={validCreditCardContent} paragraphClassForIndex={() => 'text-copy text-sm font-body leading-6'} />
              </Typography>
            </div>
          </CardInfo>
        </div>
      )}

      {/* 3-field row: Expiry | Security Code | Postal Code */}
      <div className="flex flex-col mt-6 space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
        <div className="flex-1">
          <label className="block text-copy text-xs font-semibold font-body mb-2" ref={expiryRef} htmlFor="ExpiryDate">
            <Typography variant={{ size: 'h6' }}>{expiryDateLabel}</Typography>
          </label>
          <div id="ExpiryDate" className={`relative bg-white ${expiryErrorMessage ? 'border-error' : 'border-black'} border-solid border h-12 rounded-sm`} aria-label={expiryDateLabel} />
          {expiryErrorMessage && (
            <div className="flex items-center gap-2 mt-1 w-full px-4 py-3" style={{ backgroundColor: 'var(--color-error-light)' }}>
              <Icon icon="ExclamationOctagon" className="h-4" variant={{ color: 'danger' }} />
              <Typography variant={{ size: 'small', colour: 'danger' }}>{expiryErrorMessage}</Typography>
            </div>
          )}
        </div>
        <div className="flex-1">
          <label className="block text-unselected-text text-xs font-semibold font-body mb-1" ref={cvvRef} htmlFor="Cvv">
            <span className="flex items-center gap-1">
              <Typography variant={{ size: 'h6' }}>{labelSecurityCode}</Typography>
              {cvvTooltip && <Tooltip content={cvvTooltip} inline position="above" tokens={{ minWidth: 163 }} />}
            </span>
          </label>
          <div id="Cvv" className={`relative bg-white ${cvvErrorMessage ? 'border-error' : 'border-black'} border-solid border h-12 rounded-sm`} aria-label={labelSecurityCode} />
          <div className="flex items-center" data-testid="payment-creditSecurity-error">
            {cvvErrorMessage && (
              <div className="flex items-center gap-2 mt-1 w-full px-4 py-3" style={{ backgroundColor: 'var(--color-error-light)' }}>
                <Icon icon="ExclamationOctagon" className="h-4" variant={{ color: 'danger' }} />
                <Typography variant={{ size: 'small', colour: 'danger' }}>{cvvErrorMessage}</Typography>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1" ref={postalRef}>
          <label className="block text-copy text-xs font-semibold font-body mb-1" htmlFor="postalCode">
            <span className="flex items-center gap-1">
              <Typography variant={{ size: 'h6' }}>{postalCodeLabel}</Typography>
              {postalCodeTooltip && <Tooltip content={postalCodeTooltip} inline position="above" tokens={{ minWidth: 143 }} />}
            </span>
          </label>
          <input
            type="text"
            id="postalCode"
            value={valuePostal}
            onBlur={onBlurPostal}
            autoComplete="off"
            maxLength={7}
            onChange={(e) => onChangePostal(e.target.value)}
            onFocus={onFocusPostal}
            data-testid="payment-epsPostal"
            className={`bg-white w-full ${postalErrorMessage ? 'border-error' : 'border-black'} border-solid border h-12 rounded-sm px-3 pt-1 text-[#394056] caret-black focus:outline-none focus:ring-0 focus:border-black`}
          />
          {postalErrorMessage && (
            <div className="flex items-center gap-2 mt-1 w-full px-4 py-3" style={{ backgroundColor: 'var(--color-error-light)' }} data-testid="payment-epsPostal">
              <Icon icon="ExclamationOctagon" className="h-4" variant={{ color: 'danger' }} />
              <Typography variant={{ size: 'small', colour: 'danger' }}>{postalErrorMessage}</Typography>
            </div>
          )}
        </div>
      </div>

      {/* Billing Address / Complete Address */}
      <div className="mt-6 relative z-10" ref={billingRef}>
        <Autocomplete
          label={billingAddressLabel}
          value={billingAddress}
          placeholder={billingAddressPlaceholder}
          isLoading={isAddressLoading}
          loadingLabel={billingAddressLoadingLabel}
          noResults={billingAddressNoResults}
          trailingIcon="LocationMap"
          trailingIconDecorative
          trailingIconStationary
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          onBlur={handleAddressBlur}
          options={addressSuggestions}
          // Drive validation ourselves (always 'error' or null — never undefined)
          // so the kit's free-text partial-match heuristic stays off: it would
          // misfire here because we reformat the selected address (≠ the option
          // label) and clear the suggestions. handleAddressBlur owns the error.
          validation={(billingAddressErrorMessage || addressError) ? 'error' : null}
          onFocus={() => setIsAddressFocused(true)}
          testId="payment-epsBillingAddress"
        />
        {/* The suggestions panel is absolutely positioned right below the input,
            so an error block in flow underneath it gets overlapped. The kit
            hides its own feedback pill while the panel is open; do the same
            here. The panel only opens while the input has focus, so focus is
            an exact proxy for "panel is open". */}
        {billingAddressErrorMessage && !isAddressFocused && (
          <div className="flex items-center gap-2 mt-1 w-full px-4 py-3" style={{ backgroundColor: 'var(--color-error-light)' }} data-testid="payment-epsBillingAddress-error">
            <Icon icon="ExclamationOctagon" className="h-4" variant={{ color: 'danger' }} />
            <Typography variant={{ size: 'small', colour: 'danger' }}>{billingAddressErrorMessage}</Typography>
          </div>
        )}
      </div>
    </form>
  )
}

export default CreditCardFormEPS
